import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import { action } from 'satcheljs';
import { CalendarEventEntity } from '../store/schema/CalendarEventEntity';
import { DateRange } from 'owa-datetime-utils';
import { EventsCacheLockId } from 'owa-events-cache';

/**
 * Full calendar event related actions
 */
export const fetchingFullCalendarEventForLock = action(
    'CALENDAR_EVENTS_FETCHING_FULL_CALENDAR_EVENT_FOR_LOCK',
    (
        lockId: EventsCacheLockId,
        folderId: string,
        eventKey: string,
        partialCalendarEvent: CalendarEvent | null
    ) => ({
        lockId,
        folderId,
        eventKey,
        partialCalendarEvent,
    })
);

export const fetchingFullCalendarEventForLockFailed = action(
    'CALENDAR_EVENTS_FETCHING_FULL_CALENDAR_EVENT_FOR_LOCK_FAILED',
    (lockId: EventsCacheLockId, folderId: string, eventKey: string) => ({
        lockId,
        folderId,
        eventKey,
    })
);

export const fullCalendarEventLoadedForLock = action(
    'CALENDAR_EVENTS_FULL_CALENDAR_EVENT_LOADED_FOR_LOCK',
    (
        lockId: EventsCacheLockId,
        folderId: string,
        eventKey: string,
        event: CalendarEvent,
        eventScope: EventScope,
        forceOverrideAllPropertiesForEventIfAvailable: boolean
    ) => ({
        lockId,
        folderId,
        eventKey,
        event,
        eventScope,
        forceOverrideAllPropertiesForEventIfAvailable,
    })
);

export const fullCalendarEventLoaded = action(
    'CALENDAR_EVENTS_FULL_CALENDAR_EVENT_LOADED',
    (folderId: string, eventKey: string, event: CalendarEvent) => ({
        folderId,
        eventKey,
        event,
    })
);

/**
 * Update full calendar events related actions
 */
export const updateFullCalendarEventFromServer = action(
    'CALENDAR_EVENTS_UPDATE_FULL_CALENDAR_EVENT_FROM_SERVER',
    (partialCalendarEvent: CalendarEvent) => ({ partialCalendarEvent })
);

export const updateFullCalendarEventFromPartialCalendarEvent = action(
    'CALENDAR_EVENTS_UPDATE_FULL_CALENDAR_EVENT_FROM_PARTIAL_CALENDAR_EVENT',
    (partialCalendarEvent: CalendarEvent) => ({ partialCalendarEvent })
);

/**
 * Remove full calendar events related actions
 */
export const removeFullCalendarEventInfo = action(
    'CALENDAR_EVENTS_REMOVE_FULL_CALENDAR_EVENT',
    (folderId: string, eventKey: string) => ({ folderId, eventKey })
);

export const removeAllFullCalendarEventsInfoForCache = action(
    'CALENDAR_EVENTS_REMOVE_ALL_FULL_CALENDAR_EVENTS_INFO_FOR_CACHE',
    (folderId: string) => ({ folderId })
);

/**
 * Insert or Update calendar events related actions
 */

export const upsertCalendarEventsToEventsCache = action(
    'ADD_CALENDAR_EVENTS_TO_EVENTS_CACHE',
    (folderId: string, calendarEvents: CalendarEvent[], forceReplace: boolean = false) => ({
        folderId,
        calendarEvents,
        forceReplace,
    })
);

export const addCalendarEventsWithinCurrentLockedDateRanges = action(
    'ADD_CALENDAR_EVENTS_WITHING_CURRENT_LOCKED_DATE_RANGES',
    (folderId: string, calendarEvents: CalendarEvent[]) => ({ folderId, calendarEvents })
);

/**
 * Remove calendar events related actions
 */
export const removeCalendarEventWithInstanceKeyFromEventsCache = action(
    'REMOVE_CALENDAR_EVENT_WITH_INSTANCE_KEY_FROM_EVENTS_CACHE',
    (folderId: string, instanceKey: string) => ({ folderId, instanceKey })
);

export const internalRemoveCalendarEventWithInstanceKeyFromEventsCache = action(
    'INTERNAL_REMOVE_CALENDAR_EVENT_WITH_INSTANCE_KEY_FROM_EVENTS_CACHE',
    (
        folderId: string,
        instanceKey: string,
        onEventsRemoved: (removedEvents: CalendarEventEntity[]) => void
    ) => ({ folderId, instanceKey, onEventsRemoved })
);

export const removeCalendarEventsFromEventsCacheMatchingFilter = action(
    'REMOVE_CALENDAR_EVENTS_FROM_EVENTS_CACHE_MATCHING_FILTER',
    (folderId: string, predicate: (event: CalendarEventEntity) => boolean) => ({
        folderId,
        predicate,
    })
);

export const internalRemoveCalendarEventsFromEventsCacheMatchingFilter = action(
    'INTERNAL_REMOVE_CALENDAR_EVENTS_FROM_EVENTS_CACHE_MATCHING_FILTER',
    (
        folderId: string,
        predicate: (event: CalendarEventEntity) => boolean,
        onEventsRemoved: (removedEvents: CalendarEventEntity[]) => void
    ) => ({ folderId, predicate, onEventsRemoved })
);

/**
 * Other events cache related actions
 */
export const releaseEventsCacheLock = action(
    'CALENDAR_EVENTS_RELEASE_LOCK',
    (lockId: EventsCacheLockId) => ({ lockId })
);

export const calendarEventsInEventsCacheReloaded = action(
    'CALENDAR_EVENTS_IN_EVENTS_CACHE_RELOADED',
    (folderId: string, eventsReloaded: CalendarEvent[], forceReload: boolean = false) => ({
        folderId,
        eventsReloaded,
        forceReload,
    })
);

export const calendarEventsInDateRangeLoadedForLock = action(
    'CALENDAR_EVENTS_IN_DATE_RANGE_LOADED_FOR_LOCK',
    (
        lockId: EventsCacheLockId,
        folderId: string,
        dateRange: DateRange,
        calendarEvents: CalendarEvent[]
    ) => ({ lockId, folderId, dateRange, calendarEvents })
);

export const internalDeleteEventsCache = action(
    'INTERNAL_DELETE_EVENTS_CACHE',
    (folderId: string) => ({ folderId })
);

export const fullCalendarEventWasUsed = action(
    'CALENDAR_EVENTS_FULL_CALENDAR_EVENT_WAS_USED',
    (lockId: EventsCacheLockId, eventKey: string) => ({
        lockId,
        eventKey,
    })
);

export const updateItem = action(
    'UpdateItem',
    (existingItem: CalendarEvent, updatedItem: CalendarEvent) => ({
        existingItem,
        updatedItem,
    })
);
